import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RelativeTime } from '@/components/ui/relative-time'
import { HelpCircle, Send, CheckCircle2, XCircle } from 'lucide-react'
import type { Message, QuestionsData, Question } from '@/types/conversations'

interface QuestionsMessageProps {
  message: Message
  onSubmitAnswers: (messageId: number, answers: Record<string, string | string[]>) => Promise<void>
  isProcessing?: boolean
}

function getContentWithoutQuestions(content: string): string {
  return content
    .replace(/<clarifying_questions>[\s\S]*?<\/clarifying_questions>/g, '')
    .replace(/<report>[\s\S]*?<\/report>/gi, '')
    .replace(/<level_upgrade_suggestion>.*?<\/level_upgrade_suggestion>/gi, '')
    .replace(/<next_step_suggestions>[\s\S]*?<\/next_step_suggestions>/gi, '')
    .trim()
}

// Get the label for a selected option
function getOptionLabel(question: Question, value: string): string {
  if (question.type === 'text') return value
  // Match by value first, then by label (handles fallback case where label was used as value)
  const option = question.options?.find((o) => (o.value || o.label) === value)
  return option?.label || value
}

// Format the answer for display
function formatAnswerDisplay(question: Question, answer: string | string[]): string {
  if (question.type === 'checkbox' && Array.isArray(answer)) {
    return answer.map((v) => getOptionLabel(question, v)).join(', ')
  }
  if (question.type === 'radio' && typeof answer === 'string') {
    return getOptionLabel(question, answer)
  }
  // Text type or fallback
  return String(answer)
}

// Interactive radio question with "Other" option
function RadioQuestion({
  question,
  value,
  otherValue,
  onChange,
  onOtherChange
}: {
  question: Question
  value: string
  otherValue: string
  onChange: (value: string) => void
  onOtherChange: (value: string) => void
}) {
  const { t } = useTranslation('conversations')
  const isOtherSelected = value === '__other__'

  return (
    <div className="space-y-2">
      <RadioGroup value={value} onValueChange={onChange}>
        {question.options?.map((option) => {
          // Fallback to label if value is missing (defensive against malformed data from Claude)
          const optionValue = option.value || option.label
          return (
            <div
              key={optionValue}
              className="flex items-start space-x-2 px-3 py-2 rounded-lg border border-border hover:bg-accent/50 transition-colors cursor-pointer"
              onClick={() => onChange(optionValue)}
            >
              <RadioGroupItem value={optionValue} id={`${question.id}-${optionValue}`} className="mt-0.5" />
              <Label htmlFor={`${question.id}-${optionValue}`} className="cursor-pointer flex-1 text-sm">
                {option.label}
              </Label>
            </div>
          )
        })}
        {/* Other option */}
        <div
          className={`flex items-start space-x-2 px-3 py-2 rounded-lg border transition-colors cursor-pointer ${
            isOtherSelected ? 'border-primary bg-accent/50' : 'border-border hover:bg-accent/50'
          }`}
          onClick={() => onChange('__other__')}
        >
          <RadioGroupItem value="__other__" id={`${question.id}-other`} className="mt-0.5" />
          <div className="flex-1">
            <Label htmlFor={`${question.id}-other`} className="cursor-pointer text-sm block">
              {t('chat.other')}
            </Label>
            {isOtherSelected && (
              <Input
                value={otherValue}
                onChange={(e) => onOtherChange(e.target.value)}
                placeholder={t('chat.pleaseSpecify')}
                className="mt-2"
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            )}
          </div>
        </div>
      </RadioGroup>
    </div>
  )
}

// Interactive checkbox question with "Other" option
function CheckboxQuestion({
  question,
  values,
  otherValue,
  onChange,
  onOtherChange
}: {
  question: Question
  values: string[]
  otherValue: string
  onChange: (values: string[]) => void
  onOtherChange: (value: string) => void
}) {
  const { t } = useTranslation('conversations')
  const handleToggle = (optionValue: string) => {
    if (values.includes(optionValue)) {
      onChange(values.filter((v) => v !== optionValue))
    } else {
      onChange([...values, optionValue])
    }
  }

  const isOtherSelected = values.includes('__other__')

  return (
    <div className="space-y-2">
      {question.options?.map((option) => {
        // Fallback to label if value is missing (defensive against malformed data from Claude)
        const optionValue = option.value || option.label
        return (
          <div
            key={optionValue}
            className="flex items-start space-x-2 px-3 py-2 rounded-lg border border-border hover:bg-accent/50 transition-colors cursor-pointer"
            onClick={() => handleToggle(optionValue)}
          >
            <Checkbox
              id={`${question.id}-${optionValue}`}
              checked={values.includes(optionValue)}
              className="mt-0.5 pointer-events-none"
            />
            <Label htmlFor={`${question.id}-${optionValue}`} className="cursor-pointer flex-1 text-sm pointer-events-none">
              {option.label}
            </Label>
          </div>
        )
      })}
      {/* Other option */}
      <div
        className={`flex items-start space-x-2 px-3 py-2 rounded-lg border transition-colors cursor-pointer ${
          isOtherSelected ? 'border-primary bg-accent/50' : 'border-border hover:bg-accent/50'
        }`}
        onClick={() => handleToggle('__other__')}
      >
        <Checkbox
          id={`${question.id}-other`}
          checked={isOtherSelected}
          className="mt-0.5 pointer-events-none"
        />
        <div className="flex-1">
          <Label htmlFor={`${question.id}-other`} className="cursor-pointer text-sm block pointer-events-none">
            {t('chat.other')}
          </Label>
          {isOtherSelected && (
            <Input
              value={otherValue}
              onChange={(e) => onOtherChange(e.target.value)}
              placeholder={t('chat.pleaseSpecify')}
              className="mt-2"
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          )}
        </div>
      </div>
    </div>
  )
}

// Interactive text question
function TextQuestion({
  question,
  value,
  onChange
}: {
  question: Question
  value: string
  onChange: (value: string) => void
}) {
  const { t } = useTranslation('conversations')
  return (
    <Input
      id={question.id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={question.placeholder || t('chat.enterYourAnswer')}
      className="w-full"
    />
  )
}

export function QuestionsMessage({ message, onSubmitAnswers, isProcessing = false }: QuestionsMessageProps) {
  const { t } = useTranslation('conversations')
  // Use questions_data from backend (parsed during message creation)
  const questionsData: QuestionsData | null = message.questions_data || null
  const contentWithoutQuestions = getContentWithoutQuestions(message.content)

  // Initialize with saved answers if available
  const savedAnswers = message.questions_answers || {}
  const [answers, setAnswers] = useState<Record<string, string | string[]>>(savedAnswers)
  const [otherTexts, setOtherTexts] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Form is locked if questions were answered or skipped
  const isLocked = (message.questions_answered ?? false) || (message.form_skipped ?? false) || (message.questions_superseded ?? false)

  const updateAnswer = (questionId: string, value: string | string[]) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }))
  }

  const updateOtherText = (questionId: string, value: string) => {
    setOtherTexts((prev) => ({ ...prev, [questionId]: value }))
  }

  // Build final answers, replacing __other__ with actual text
  const buildFinalAnswers = (): Record<string, string | string[]> => {
    const finalAnswers: Record<string, string | string[]> = {}

    for (const [questionId, answer] of Object.entries(answers)) {
      if (Array.isArray(answer)) {
        // Checkbox - replace __other__ with actual text
        finalAnswers[questionId] = answer.map((v) =>
          v === '__other__' ? (otherTexts[questionId] || 'Other') : v
        )
      } else if (answer === '__other__') {
        // Radio - replace __other__ with actual text
        finalAnswers[questionId] = otherTexts[questionId] || 'Other'
      } else {
        finalAnswers[questionId] = answer
      }
    }

    return finalAnswers
  }

  const handleSubmit = async () => {
    if (!questionsData) return

    setIsSubmitting(true)
    try {
      const finalAnswers = buildFinalAnswers()
      console.log('📝 Submitting answers:', finalAnswers)
      console.log('📝 Current answers state:', answers)
      console.log('📝 Other texts:', otherTexts)
      await onSubmitAnswers(message.id, finalAnswers)
    } catch (error) {
      console.error('Failed to submit answers:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const hasAnswers = Object.values(answers).some((v) => {
    if (Array.isArray(v)) return v.length > 0
    return v && v.trim() !== ''
  })

  if (!questionsData) {
    // Fallback: render as regular assistant message if parsing fails
    return (
      <div className="flex gap-3 animate-in fade-in slide-in-from-left-2 duration-300 min-w-0 mt-4">
        <div className="flex flex-col gap-1 min-w-0 flex-1 max-w-full">
          <div className="prose prose-sm dark:prose-invert max-w-none text-sm [overflow-wrap:anywhere]">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{getContentWithoutQuestions(message.content)}</ReactMarkdown>
          </div>
          <RelativeTime timestamp={message.created_at} className="text-xs text-muted-foreground" />
        </div>
      </div>
    )
  }

  return (
    <div className="flex gap-3 animate-in fade-in slide-in-from-left-2 duration-300 min-w-0 mt-4">
      <div className="flex flex-col gap-2 min-w-0 flex-1 max-w-full">
        {/* Show any content before the questions */}
        {contentWithoutQuestions && (
          <div className="prose prose-sm dark:prose-invert max-w-none text-sm mb-2 [overflow-wrap:anywhere]">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{contentWithoutQuestions}</ReactMarkdown>
          </div>
        )}

        <div className="relative group/questions">
          {/* Icon badge on top-left corner of card */}
          <div className={`absolute -top-2 -left-2 h-6 w-6 rounded-full flex items-center justify-center border transition-colors z-10 ${
            message.questions_answered
              ? 'bg-green-50 dark:bg-green-950 border-green-500/30 group-hover/questions:bg-green-100 dark:group-hover/questions:bg-green-900'
              : message.questions_superseded
              ? 'bg-gray-50 dark:bg-gray-950 border-gray-500/30 group-hover/questions:bg-gray-100 dark:group-hover/questions:bg-gray-900'
              : 'bg-blue-50 dark:bg-slate-900 border-blue-500/20 group-hover/questions:bg-muted dark:group-hover/questions:bg-slate-800'
          }`}>
            {message.questions_answered ? (
              <CheckCircle2 className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
            ) : message.questions_superseded ? (
              <XCircle className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
            ) : (
              <HelpCircle className="h-3.5 w-3.5 text-blue-500" />
            )}
          </div>

          <Card className={`p-4 gap-4 min-w-0 max-w-full ${
            message.questions_answered
              ? 'border-green-500/30 bg-green-50/30 dark:bg-green-950/10'
              : message.questions_superseded
              ? 'border-gray-500/30 bg-gray-50/30 dark:bg-gray-950/10'
              : 'border-blue-500/20 bg-blue-50/50 dark:bg-blue-950/20'
          }`}>
            <span className="text-sm font-semibold text-foreground/80 min-w-0">
              {message.questions_answered
                ? t('chat.questionsAnswered')
                : message.questions_superseded
                ? t('chat.questionsNoLongerRelevant')
                : questionsData.intro || t('chat.clarifyingQuestions')}
            </span>

          <div className="space-y-3">
            {questionsData.questions.map((question, index) => {
              const savedAnswer = savedAnswers[question.id]
              const currentAnswer = answers[question.id]

              return (
                <div key={question.id} className="space-y-2">
                  <div className="flex items-start gap-2">
                    <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                      message.questions_answered
                        ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                        : message.questions_superseded
                        ? 'bg-gray-500/10 text-gray-600 dark:text-gray-400'
                        : 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
                    }`}>
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <Label className="text-sm font-medium">{question.label}</Label>
                      {question.description && !isLocked && (
                        <p className="text-xs text-muted-foreground mt-0.5">{question.description}</p>
                      )}
                    </div>
                  </div>

                  <div className="ml-8">
                    {isLocked ? (
                      // Read-only display - show answers if answered, hide if superseded
                      message.questions_answered && savedAnswer ? (
                        <div className="p-3 rounded-lg border border-green-500/50 bg-green-50/50 dark:bg-green-950/20">
                          <p className="text-sm font-medium">
                            {formatAnswerDisplay(question, savedAnswer)}
                          </p>
                        </div>
                      ) : null
                    ) : (
                      // Interactive form inputs with "Other" option
                      <>
                        {question.type === 'radio' && (
                          <RadioQuestion
                            question={question}
                            value={(currentAnswer as string) || ''}
                            otherValue={otherTexts[question.id] || ''}
                            onChange={(value) => updateAnswer(question.id, value)}
                            onOtherChange={(value) => updateOtherText(question.id, value)}
                          />
                        )}
                        {question.type === 'checkbox' && (
                          <CheckboxQuestion
                            question={question}
                            values={(currentAnswer as string[]) || []}
                            otherValue={otherTexts[question.id] || ''}
                            onChange={(values) => updateAnswer(question.id, values)}
                            onOtherChange={(value) => updateOtherText(question.id, value)}
                          />
                        )}
                        {question.type === 'text' && (
                          <TextQuestion
                            question={question}
                            value={(currentAnswer as string) || ''}
                            onChange={(value) => updateAnswer(question.id, value)}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              )
            })}

            {!isLocked && (
              <Button onClick={handleSubmit} disabled={isSubmitting || isProcessing || !hasAnswers} className="w-full gap-2 mt-4">
                <Send className="h-4 w-4" />
                {isSubmitting ? t('chat.submitting') : isProcessing ? t('chat.pleaseWaitProcessing') : t('chat.submitAnswers')}
              </Button>
            )}
          </div>
          </Card>
        </div>

        <RelativeTime timestamp={message.created_at} className="text-xs text-muted-foreground" />
      </div>
    </div>
  )
}
